import { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import { Trophy, Medal, Crown, ArrowRight, Share2, Check } from 'lucide-react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { api } from '../../lib/api';
import { SectionLabel, Reveal } from '../Section';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function SeasonRecap() {
  const [completed, setCompleted] = useState([]);
  const [winners, setWinners] = useState([]);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    let alive = true;
    const load = () => {
      api
        .tournaments('completed')
        .then((d) => {
          if (!alive) return;
          const list = d.tournaments || [];
          setCompleted(list);
          if (list[0]) {
            axios
              .get(`${API}/tournaments/${list[0].id}/results`)
              .then((r) => {
                if (alive) setWinners(r.data.winners || []);
              })
              .catch(() => {});
          }
        })
        .catch(() => {
          if (alive) setCompleted([]);
        });
    };
    load();
    const i = setInterval(load, 60000);
    return () => {
      alive = false;
      clearInterval(i);
    };
  }, []);

  if (!completed || completed.length === 0) return null;

  const latest = completed[0];

  const handleShare = async () => {
    const shareData = {
      title: `${latest.title} — KROY Esports Champions`,
      text: `Check out the winners for ${latest.title} on KROY Esports!`,
      url: window.location.origin + '/tournaments',
    };
    if (navigator.share) {
      try {
        await navigator.share(shareData);
      } catch (err) {
        // User dismissed
      }
    } else {
      await navigator.clipboard.writeText(shareData.url);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  };

  return (
    <section className="py-16 sm:py-24 border-y border-white/5 bg-gradient-to-b from-[#0d0d0d] via-[#121520] to-[#0d0d0d]" data-testid="season-recap-section">
      <div className="max-w-7xl mx-auto px-5 sm:px-8">
        <div className="flex items-center justify-between gap-4 flex-wrap">
          <SectionLabel testid="season-recap-label">Hall of Champions</SectionLabel>
          <button
            onClick={handleShare}
            data-testid="season-recap-share-btn"
            className="inline-flex items-center gap-2 text-xs font-bold uppercase tracking-wider text-[#9e9e9e] hover:text-white border border-white/10 hover:border-[#e8173a]/50 px-3.5 py-1.5 rounded-lg transition-all"
          >
            {copied ? (
              <>
                <Check className="w-3.5 h-3.5 text-[#00c853]" />
                <span className="text-[#00c853]">Link Copied</span>
              </>
            ) : (
              <>
                <Share2 className="w-3.5 h-3.5 text-[#e8173a]" />
                <span>Share Results</span>
              </>
            )}
          </button>
        </div>

        <div className="mt-8 grid lg:grid-cols-[1.2fr_1fr] gap-8 items-center">
          <Reveal>
            <div className="card-surface p-7 sm:p-10 relative overflow-hidden">
              <div className="absolute top-0 right-0 w-64 h-64 bg-[#e8173a]/10 blur-3xl rounded-full pointer-events-none" />
              <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-[#e8173a]/15 border border-[#e8173a]/30 mb-4">
                <Crown className="w-3.5 h-3.5 text-[#e8173a]" />
                <span className="text-[10px] font-extrabold uppercase tracking-widest text-white">Latest Concluded Battle</span>
              </div>
              <h2 className="font-display text-white text-2xl sm:text-4xl leading-tight" data-testid="season-recap-title">
                {latest.title}
              </h2>
              <div className="mt-4 flex flex-wrap items-center gap-6 text-sm">
                <div>
                  <span className="text-[10px] font-bold uppercase tracking-widest text-[#8a8a8a] block">Prize Pool</span>
                  <span className="text-xl font-black italic text-[#e8173a]">₹{Number(latest.prize_pool || 0).toLocaleString('en-IN')}</span>
                </div>
                <div>
                  <span className="text-[10px] font-bold uppercase tracking-widest text-[#8a8a8a] block">Format</span>
                  <span className="text-sm font-bold text-white">{latest.game_mode || latest.game || 'Squad'}</span>
                </div>
                <div>
                  <span className="text-[10px] font-bold uppercase tracking-widest text-[#8a8a8a] block">Squads Competed</span>
                  <span className="text-sm font-bold text-white">{latest.slots_joined} Teams</span>
                </div>
              </div>
              <div className="mt-6 flex gap-3">
                <Link
                  to="/tournaments"
                  data-testid="season-recap-view-btn"
                  className="inline-flex items-center gap-2 bg-[#e8173a] hover:bg-[#c0102e] text-white text-xs font-extrabold uppercase tracking-wider px-5 py-3 rounded-lg transition-all active:scale-95"
                >
                  View Full Standings
                  <ArrowRight className="w-3.5 h-3.5" />
                </Link>
              </div>
            </div>
          </Reveal>

          {winners.length > 0 && (
            <Reveal delay={0.1}>
              <div className="card-surface p-6 sm:p-8 space-y-4" data-testid="recap-distribution">
                <p className="text-xs font-extrabold uppercase tracking-[0.25em] text-[#8a8a8a] flex items-center gap-2">
                  <Trophy className="w-4 h-4 text-[#e8173a]" />
                  Prize Pool Distribution
                </p>
                <div className="space-y-2.5">
                  {winners.slice(0, 3).map((w, i) => (
                    <div
                      key={w.rank}
                      data-testid={`recap-winner-${w.rank}`}
                      className={`flex items-center gap-3 p-3 rounded-lg ${
                        i === 0 ? 'bg-[#e8173a]/15 border border-[#e8173a]/40' : 'bg-white/5 border border-white/5'
                      }`}
                    >
                      <span
                        className={`w-7 h-7 rounded-md flex items-center justify-center font-black text-xs ${
                          i === 0 ? 'bg-[#e8173a] text-white' : 'bg-white/10 text-white'
                        }`}
                      >
                        {w.rank}
                      </span>
                      <div className="flex-1 min-w-0">
                        <p className="text-white font-bold text-sm truncate">{w.team}</p>
                        <p className="text-[11px] text-[#8a8a8a]">
                          {['Champion', 'Runner Up', '3rd Place'][i] || `#${w.rank}`}
                          {w.points > 0 && ` • ${w.points} pts`}
                        </p>
                      </div>
                      {w.prize > 0 && (
                        <span className="text-sm font-bold text-[#00c853]">₹{Number(w.prize).toLocaleString('en-IN')}</span>
                      )}
                      {i === 0 ? (
                        <Crown className="w-4 h-4 text-[#e8173a]" />
                      ) : (
                        <Medal className="w-4 h-4 text-[#8a8a8a]" />
                      )}
                    </div>
                  ))}
                </div>
              </div>
            </Reveal>
          )}
        </div>
      </div>
    </section>
  );
}
